import moment from 'moment';
import BTC from '@/assets/svg/cryptos/btc.svg';
import NOT from '@/assets/svg/cryptos/not.svg';
import ETH from '@/assets/svg/cryptos/eth.svg';
import BNB from '@/assets/svg/cryptos/bnb.svg';
import XRP from '@/assets/svg/cryptos/xrp.svg';
import DOGE from '@/assets/svg/cryptos/doge.svg';
import SOL from '@/assets/svg/cryptos/sol.svg';
import BCH from '@/assets/svg/cryptos/bch.svg';
import AVAX from '@/assets/svg/cryptos/avax.svg';
import ADA from '@/assets/svg/cryptos/ada.svg';
import {
    oCoinMap,
    oLongShort,
    oPnlFlat,
    oPairsMap,
    oTable4Types,
    oTriggerType,
    oWavesContractStatus,
    WavesContractStateAll,
} from '@/socket/game/dto/waves.dto';
import type {
    OneTick,
    TikcArr,
    WavesStatusColor,
} from '@/socket/game/dto/waves.dto';

export type {
    OneTick,
    TikcArr,
}

export {
    oCoinMap,
    oLongShort,
    oPnlFlat,
    oPairsMap,
    oTable4Types,
    oTriggerType,
    oWavesContractStatus, 
    WavesContractStateAll,
};

// ↓ 表格行输赢表
export const aPriceMoveStandard = Object.freeze([ 
    1_000, 500, 100, 50, 10, 5, 1, .5, .1, .05, .01,
    0,
    -.01, -.05, -.1, -.5, -1, -5, -10, -50, -100,
]);

export const fFlatCoeff_Ze = 5 / 10_000; // = 5e-4 = 万分之五 
export const fMaxWagerAmount_K = 1_000_000; // 1e6; 
export const fsubstitute_K = 1_000_000; // 一百万，1e6; 
export const iDefaultMulti = 20; // 默认下注倍数  

// ↓ 折扣提示信息 
export const aDisCountMap = [
    '> $2,500,000 - 60%',
    '> $1,000,000 - 50%',
    '> $250,000 - 40%',
    '> $100,000 - 30%',
    '> $10,000 - 20%',
    '> $1,000 - 10%',
    '> $100 - 5%',
];

// ↓ 币种列表（用于下拉菜单）  
export const aCoinList = [{
    name: oCoinMap.BTC,
    pairName: '', // 示例值：AAA_USDT ◆ 会在尾部的 map() 中补充 
    fullName: 'Bitcoin',
    icon: BTC,
}, {
    name: oCoinMap.NOT,
    fullName: 'NOTcoin',
    icon: NOT,
}, {
    name: oCoinMap.ETH,
    fullName: 'Ethereum',
    icon: ETH,
}, {
    name: oCoinMap.BNB,
    fullName: 'Binance Coin',
    icon: BNB,
}, {
    name: oCoinMap.XRP,
    fullName: 'Ripple',
    icon: XRP,
}, {
    name: oCoinMap.DOGE,
    fullName: 'Dogecoin',
    icon: DOGE,
}, {
    name: oCoinMap.SOL,
    fullName: 'Solana',
    icon: SOL,
}, {
    name: oCoinMap.BCH,
    fullName: 'Bitcoin Cash',
    icon: BCH,
}, {
    name: oCoinMap.AVAX,
    fullName: 'Avalanche',
    icon: AVAX,
}, {
    name: oCoinMap.ADA,
    fullName: 'Cardano',
    icon: ADA,
}].map(cur=>{
    cur['pairName'] ||= `${cur.name}_USDT`; 
    return cur; 
});

// ↓ 默认币种 
export const oDefaultCoin = aCoinList.find(cur=>{
    return cur.name === oCoinMap.BTC;
});

// ↓ 特殊币种 CorgiWaves 
export const oSpecialCoin = aCoinList.find(cur=>{
    // TODO 临时值
    return false;  
    // return cur.name === oCoinMap.DOGE;
});

const sec = 1_000; // 1秒
const mnt = sec * 60; // 1分 
const hr = mnt * 60; // 1小时
export const iDaySeconds = 24 * 60 * 60; // 1天24小时的秒数量（用于定位到每日0点）

export const oTimeMS = Object.freeze({
    SECOND: sec,
    MINUTE: mnt,
    HOUR: hr,
    DAY: 24 * hr,
});

export const oChartConfig01_tt = Object.assign({
    area: "Area",
    candlestick: "Candlestick",
    // ↑ 两个图表类型 --------------------
    bar: "Bar",
    line: "Line",
    custom: "Custom",
    baseline: "Baseline",
    histogral: "Histogram", 
});


// ↓ 图表粒度选项，对象（值全是毫秒） 
export const oTickOptions = {
    'Tick': 0.5 * sec ,
    '5s': 5 * sec,
    '15s': 15 * sec,
    '30s': 30 * sec,
    // -----------------------------
    '1m': 1 * mnt,
    '3m': 3 * mnt,
    '5m': 5 * mnt,
    '15m': 15 * mnt,
    '30m': 30 * mnt,
    // -----------------------------
    '1h': 1 * hr,
    '2h': 2 * hr,
    '4h': 4 * hr,
    '6h': 6 * hr,
    '8h': 8 * hr,
    '12h': 12 * hr,
};

// ↓ 图表粒度选项对象，升序 
export const aTickOptions = (()=>{
    const aResult = Object.entries(oTickOptions).reduce(
        (aResult, aCur) => {
            aResult.push({
                label: aCur[0], 
                value: aCur[1], 
            });
            return aResult;
        },
        [
            // ↓ 排序后的示例： 这里的 value 将做为 rangeMs 使用  
            // {label: 'Tick', value: 0.5 * sec }, 
            // {label: '5s', value: 5 * sec},
            // {label: '15s', value: 15 * sec},
        ],
    );
    aResult.sort((aa, bb) => { 
        return aa.value - bb.value;
    });
    return aResult;
})();

const oRed = {
    color: 'rgb(237, 29, 73)', // ← 跌红色 === #ed1d49 
    colorShallow: 'rgba(237, 29, 73, 0.7)',
    icon: "icon-double-chevron-down", // 这2类图标似乎相同没区别 
    icon02: "double-arrow-down", // 可用于： <IconSvgUse/> 
} as WavesStatusColor;
const oGreen = {
    color: "rgb(27, 184, 61)", // 上涨绿色 === #1bb83d 
    colorShallow: "rgba(27, 184, 61, 0.7)",
    icon: "icon-double-chevron-up", 
    icon02: "double-arrow-up", 
} as WavesStatusColor;

// ↓ 0红色，1绿色
export const oColorInfo = {
    '0': oRed,
    '1': oGreen,
    'red': oRed,
    'green': oGreen,
    'short': oRed,
    'long': oGreen,
};

export type ChartOptions = typeof chartOptions;
export const chartOptions = {
    "autoSize": true,
    "layout": {
        "background": {
            "color": "transparent",
        },
        "textColor": "#3C485C",
        attributionLogo: false, // 关闭 logo 显示  
    },
    "grid": {
        "vertLines": {
            "color": "rgba(147, 172, 211, 0.05)"
        },
        "horzLines": {
            "color": "rgba(147, 172, 211, 0.05)"
        }
    },
    "rightPriceScale": {
        "borderColor": "rgba(147, 172, 211, 0.1)", // 右侧轴竖线颜色  
    },
    "crosshair": {
        "mode": 0, // 0, // 最初为 0 
    },
    "localization": {
        timeFormatter: (timeStampMS) => {
            timeStampMS *= 1_000;
            return moment(timeStampMS).format("D MMM YY HH:mm:ss");
        },
    },
    "timeScale": {
        "borderColor": "rgba(147, 172, 211, 0.1)",
        "ticksVisible": true,
        "timeVisible": true,
        "secondsVisible": true,
        "rightOffset": 5,
        tickMarkFormatter: (timeStampMS) => {
            timeStampMS *= 1_000;
            return moment(timeStampMS).format("HH:mm:ss");
        },
    },
};


export type SeriesOptions = typeof seriesOptions;
export const seriesOptions = {
    "backgroundColor": "transparent",
    "bottomColor": "rgba(147, 172, 211, 0)",
    "lineColor": "rgba(147, 172, 211, 1)",
    "topColor": "rgba(147, 172, 211, 0.4)",
    "lineType": 2,
    "lineWidth": 2,
    "visible": true,
    "priceLineVisible": true,
    "priceLineColor": oColorInfo.red.color,
    "lastPriceAnimation": 1,
    "priceFormat": {
        "type": "price", // 常量固定值 ✔ 
        "precision": 2, // 切换币种时需要修改 
        "minMove": 0.01, // 切换币种时需要修改 
    },
    "downColor": oColorInfo.red.color,
    "borderDownColor": oColorInfo.red.color,
    "wickDownColor": oColorInfo.red.color,
    "upColor": oColorInfo.green.color, 
    "borderUpColor": oColorInfo.green.color, 
    "wickUpColor": oColorInfo.green.color, 
};


// ===============================================================
// ==== 此处向下是用于查阅的配置对象 ===============================
// ==== 在工程中没有实例使用 ======================================= 
// ===============================================================


// flat 费用研究 
var aaa = function(){
    // 433[{"response":{"user":{"features":{"futuresEnabled":true},"_id":"6566c714572c629a6d05e562","availableTasksCount":28,"avatar":0,"canWithdraw":true,"canWithdrawError":null,"cashback":0.05,"cohorts":{},"currencies":{"eth":{"cashbackValue":0},"usdt":{"balance":8.638616694789041,"cashbackValue":0},"funfury":{"balance":0,"cashbackValue":0},"btc":{"cashbackValue":0},"trx":{"cashbackValue":0}},"email":"lixingyang.com@gmail.com","fav":["5dbe511637d4a30ebf4cabfd","5e7b81a5d3e1db0ed10212e5"],"hadDeposit":true,"isFirstLogin":false,"isReferral":false,"isVip":false,"lang":"en","lvl":1,"maxCampaigns":20,"name":"User6976774","notifications":[],"promoCheck":false,"regDate":"2023-11-29T05:07:32.153Z","stTotalEarnedStaking":0,"timeAlerts":{},"tncVersion":"1.0","totalEarnedStaking":0,"totalWager":0,"totalWagerUsdt":0,"two_factor_enabled":false,"two_factor_reward":true,"vipConfirmShowed":false,"vipManager":null},"progress":0}}]	
    // 42["balances",{"usdt":{"balance":8.437916694789042,"cashbackValue":0}},null]	
    // 🅰️ 8.638616694789041 - 8.437916694789042 = 0.20069999999999943 
    // 费用：0.0007

    // -------
    // 42["balances",{"usdt":{"balance":7.437916694789042,"cashbackValue":0}},null]	
    // 42["balances",{"usdt":{"balance":6.437216694789042,"cashbackValue":0}},null]	 
    // 🅰️ 7.437916694789042 - 6.437216694789042 = 1.0007000000000001 
    // 费用：0.0007

    // 以上2项有效仓数据显示： flatFee: 0.0007 
    
    // ↓ 原版文字说明： 
    // 每 xx 美元的进出交易 0.035%，包括滑点 (0.035% per entry and exit trades of $20 including slippage)
};


// 平仓过程
var aaa = function(){
    var bb = [];
    bb = [
        "futures.closePosition",
        {"positionId":"66fa39c8084a1553a80485a9"},
    ];
    bb = [
        "futuresUpdate",
        {"data": {"id":"66fa39c8084a1553a80485a9","state":"cashOutProcessing"}},
        null
    ];
    bb = [
        "futuresUpdate",
        {"data":{"id":"66fa39c8084a1553a80485a9","state":"cashed","pnl":0.0011522806281990968,"roi":1.1522806281990967,"maxUserPositionAmount":100000,"maxWagerAmount":1000}},
        null,
    ]; 

    // ↓ 公共页的数据推送（本人 User6976774 
    bb = [
        "publicPosition",
        {
            "data": [
                {"userId":"6566c714572c629a6d05e562","avatar":0,"bustPrice":25.771991070250994,"closedAt":"2024-09-30T06:01:42.877Z","currency":"usdt","entryPrice":28.621499999999997,"exitPrice":28.7185,"feeType":"pnl","funding":0,"multi":10,"name":"User6976774","pair":"AVAX_USDT","pairCurrency":"avax","pnl":0.0011522806281990968,"positionAmount":1,"roi":1.1522806281990967,"state":"cashed","stopLossPrice":27.202434016738444,"takeProfitPrice":34.8435652173913,"timestamp":1727674824500,"type":"long","wager":0.1,"validated":true}
            ]
        }
    ];
    // ↓ 最后常规查询 
    bb = [
        "futures.positions",
        {"type":"active","perPage":10,"page":1,"pair":""}
    ]; 
};

// 修改仓
var aaa = function(){
    var bb = [];
    bb = [
        "futures.updatePosition",
        {"positionId":"66fa44ba084a1553a8048688","stopLossPrice":"28.9979","takeProfitPrice":"24.944","takeProfitAmount":"0.1","stopLossAmount":"0.02","selectedTriggerLoss":"value","selectedTriggerProfit":""}, 
    ];
    bb = [
        "futuresUpdate",
        {"data": {"id":"66fa44ba084a1553a8048688","state":"active"}}, // 状态没变，保持为 active 
        null,
    ];
    // ↓ 重新查询表格（建仓和修改仓都是如此） 
    bb = [
        "futures.positions",
        {"type":"active","perPage":10,"page":1,"pair":""},
    ];
};

// 收到通知被平仓 
var aaa = function(){
    var bb = [];
    bb = [
        "futuresUpdate",
        {
            "data": {
                "id": "66fa44ba084a1553a8048688",
                "state": "cashed",
                "pnl": 0.0022608665378523107,
                "roi": 2.2608665378523107,
                "maxUserPositionAmount": 100000,
                "maxWagerAmount": 1000,
            },
        },
        null,
    ];
};


// 记录用户已读条款：
var aaa = function(){
    // 提交请求 1，2 
    var aa;
    aa = ["futures.updateTermsAndConditions", {"termsAndConditionsConfirmed":true}]; 
    aa = ["profile.confirmNewTerms", {}]; 
};



// ↓ 配置对象示例值  
// ↓ 原版中这个配置对象是从服务器查询得出的 
const oTestPairsSettings = { 
    // ↓ 无效项，已停用，
    "FuryWaves_USDT": {
        // availableFees: {}, // 特殊币没有此项  
        // endOfFundingPeriod: 000, // 特殊币没有此项  
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            // ↓ 左侧键名对应资金（总仓值）的值
            // ↓ 这个 FuryWaves 币有点特殊，结果值都是 0.95 
            "1": { "0": 0.95, "1": 0.95, "5": 0.95, "10": 0.95, "50": 0.95, "100": 0.95, "500": 0.95, "0.01": 0.95, "0.05": 0.95, "0.1": 0.95, "0.5": 0.95 },
            "1000": { "0": 0.95, "0.01": 0.95, "0.05": 0.95, "0.1": 0.95, "0.5": 0.95, "1": 0.95, "5": 0.95, "10": 0.95, "50": 0.95, "100": 0.95, "500": 0.95, },
            "10000": { "0": 0.95, "1": 0.95, "5": 0.95, "10": 0.95, "50": 0.95, "100": 0.95, "500": 0.95, "0.01": 0.95, "0.05": 0.95, "0.1": 0.95, "0.5": 0.95 },
            "100000": { "0": 0.95, "1": 0.95, "5": 0.95, "10": 0.95, "50": 0.95, "100": 0.95, "500": 0.95, "0.01": 0.95, "0.05": 0.95, "0.1": 0.95, "0.5": 0.95 }
        },
        "weight": 10,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        // ↓ 用法： bustBuffer * bufferMultiplier 
        // ↓ 此值疑似有借款利息的意味
        "bustBuffer": 0.00068, 
        "bufferMultiplier": 1, // 普遍值为 1 
        "maxMultiplier": 1000,
        "precision": 2,
        "sessionFinishAt": 1725408000000
    },
    "BNB_USDT": {
        ...{
            "minWagerAmount": 0.01, // 最小下注值（本金） 
            "maxWagerAmount": 10000,
        },
        // ↓ 下方杠杆后的值应大于上方2项的值 
        ...{
            "minUserPositionAmount": 1, // 最小下注值（杠杆后） 
            "maxUserPositionAmount": 10000,
        },
        ...{
            "bufferMultiplier": 1,
            "maxMultiplier": 1000, // 杠杆率上限 
        },
        "weight": 3, // 序号，越小越靠前 
        "precision": 2, // 小数位数 
        "endOfFundingPeriod": 1725379200000, // 下一期资金费结算时间,
        "bustBuffer": 0.0005234324,
        // === ↓ pnl, flat 相关配置 =============================================== 
        "availableFees": {
            "pnl": true,
            "flat": true, 
        },
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.49998, "0.05": 0.51999, "0.1": 0.6999, "0.5": 0.7222 },
            // ↓ 杠杆后的总仓值档位： {不同涨跌幅度百分比对应的提现比例}
            // ↓ 总仓值 1千，2千，3千 都属于这个档位 
            "1000": {
                // 涨幅波动百分数档位: 提现百分比（涨得幅度小，提现比例也小，得给庄家留利润 
                "0": 0.1,
                "0.01": 0.48998,
                "0.05": 0.51112,
                "0.1": 0.6888,
                "0.5": 0.7111,
                "1": 0.8988, // 涨幅1%: 玩家利润分成高至 0.89 
                "5": 0.8994,
                "10": 0.89988, 
                "50": 0.89994, 
                "100": 0.899988,
                "500": 0.899994, 
            },
            "10000": { "0": 0.1, "1": 0.8980249802, "5": 0.8980249802, "10": 0.899801998, "50": 0.899900999, "100": 0.8999801998, "500": 0.8999900999, "0.01": 0.478999, "0.05": 0.4999, "0.1": 0.67779, "0.5": 0.6999 },
            "100000": { "0": 0.1, "1": 0.8910890911, "5": 0.8955223955, "10": 0.8991008991, "50": 0.8995502296, "100": 0.8999100099, "500": 0.899955002, "0.01": 0.45, "0.05": 0.6, "0.1": 0.8181836364, "0.5": 0.8571428571 }
        },
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005,
        },
    },
    "BTC_USDT": {
        "maxUserPositionAmount": 100000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.89, "5": 0.898, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.88, "0.05": 0.88, "0.1": 0.88, "0.5": 0.89 },
            "1000": { "0": 0.1, "1": 0.8935, "5": 0.8987, "10": 0.89935, "50": 0.89987, "100": 0.899935, "500": 0.899987, "0.01": 0.52, "0.05": 0.786, "0.1": 0.839, "0.5": 0.8872 },
            "10000": { "0": 0.1, "1": 0.88913, "5": 0.897806, "10": 0.898901, "50": 0.89978, "100": 0.89989, "500": 0.899978, "0.01": 0.405, "0.05": 0.7232, "0.1": 0.802, "0.5": 0.87852 },
            "100000": { "0": 0.1, "1": 0.8910890911, "5": 0.8955223955, "10": 0.8991008991, "50": 0.8995502296, "100": 0.8999100099, "500": 0.899955002, "0.01": 0.15, "0.05": 0.4325, "0.1": 0.45678, "0.5": 0.8571428571 }
        },
        "weight": 0,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0003416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 2,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005,
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "ETH_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.75, "0.05": 0.81, "0.1": 0.88, "0.5": 0.89 },
            "1000": { "0": 0.1, "1": 0.8988, "5": 0.8994, "10": 0.89988, "50": 0.89994, "100": 0.899988, "500": 0.899994, "0.01": 0.8, "0.05": 0.84, "0.1": 0.888, "0.5": 0.894 },
            "10000": { "0": 0.1, "1": 0.8980249802, "5": 0.8980249802, "10": 0.899801998, "50": 0.899900999, "100": 0.8999801998, "500": 0.8999900999, "0.01": 0.7376, "0.05": 0.8108, "0.1": 0.88062, "0.5": 0.8902089021 },
            "100000": { "0": 0.1, "1": 0.8910890911, "5": 0.8955223955, "10": 0.8991008991, "50": 0.8995502296, "100": 0.8999100099, "500": 0.899955002, "0.01": 0.15, "0.05": 0.6, "0.1": 0.8181836364, "0.5": 0.8571428571 }
        },
        "weight": 1,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0003416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 2,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "XRP_USDT": {
        "maxUserPositionAmount": 11000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.51, "0.05": 0.7489, "0.1": 0.81, "0.5": 0.88 },
            "1000": { "0": 0.1, "1": 0.8988, "5": 0.8994, "10": 0.89988, "50": 0.89994, "100": 0.899988, "500": 0.899994, "0.01": 0.51, "0.05": 0.84, "0.1": 0.888, "0.5": 0.894 },
            "10000": { "0": 0.1, "1": 0.8980249802, "5": 0.8980249802, "10": 0.899801998, "50": 0.899900999, "100": 0.8999801998, "500": 0.8999900999, "0.01": 0.42221, "0.05": 0.8108, "0.1": 0.88062, "0.5": 0.8902089021 },
            "100000": { "0": 0.1, "1": 0.8910890911, "5": 0.8955223955, "10": 0.8991008991, "50": 0.8995502296, "100": 0.8999100099, "500": 0.899955002, "0.01": 0.15, "0.05": 0.48889, "0.1": 0.5889, "0.5": 0.68571428 }
        },
        "weight": 4,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0003416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 5,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "SOL_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.894, "5": 0.8988, "10": 0.8994, "50": 0.89988, "100": 0.89994, "500": 0.89999, "0.01": 0.5, "0.05": 0.61, "0.1": 0.71, "0.5": 0.78 },
            "1000": { "0": 0.1, "1": 0.8916, "5": 0.89832, "10": 0.89915, "50": 0.89983, "100": 0.899915, "500": 0.8999983, "0.01": 0.46, "0.05": 0.52, "0.1": 0.59, "0.5": 0.61 },
            "10000": { "0": 0.1, "1": 0.87036, "5": 0.893912, "10": 0.896946, "50": 0.899387, "100": 0.8999387, "500": 0.89996935, "0.01": 0.204, "0.05": 0.5354, "0.1": 0.6714, "0.5": 0.84262 },
            "100000": { "0": 0.01, "1": 0.702835, "5": 0.8521876, "10": 0.8754414, "50": 0.89497866, "100": 0.89748231, "500": 0.899495332, "0.01": 0.031, "0.05": 0.13614, "0.1": 0.23651, "0.5": 0.576532 }
        },
        "weight": 6,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0004616634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 4,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "DOGE_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.75, "0.05": 0.81, "0.1": 0.88, "0.5": 0.89 },
            "1000": { "0": 0.1, "1": 0.894, "5": 0.8988, "10": 0.8994, "50": 0.89988, "100": 0.89994, "500": 0.899994, "0.01": 0.5, "0.05": 0.8, "0.1": 0.84, "0.5": 0.888 },
            "10000": { "0": 0.1, "1": 0.8892, "5": 0.89782, "10": 0.8989, "50": 0.89978, "100": 0.899978, "500": 0.8999900999, "0.01": 0.41, "0.05": 0.724, "0.1": 0.802, "0.5": 0.8786 },
            "100000": { "0": 0.05, "1": 0.84759, "5": 0.889006, "10": 0.89447, "50": 0.8988884, "100": 0.89988872, "500": 0.899955002, "0.01": 0.125, "0.05": 0.4024, "0.1": 0.5561, "0.5": 0.80096 }
        },
        "weight": 5,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0003416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 6,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "BCH_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.38, "5": 0.42, "10": 0.46, "50": 0.4888, "100": 0.52289, "500": 0.568999, "0.01": 0.22, "0.05": 0.32, "0.1": 0.34, "0.5": 0.36 },
            "1000": { "0": 0.1, "1": 0.68889, "5": 0.69999, "10": 0.7222, "50": 0.78889, "100": 0.82229, "500": 0.888889, "0.01": 0.14, "0.05": 0.422, "0.1": 0.575, "0.5": 0.5666 },
            "10000": { "0": 0.01, "1": 0.59998, "5": 0.62229, "10": 0.68889, "50": 0.78889, "100": 0.88888, "500": 0.8999900999, "0.01": 0.017, "0.05": 0.081, "0.1": 0.1499, "0.5": 0.44478 },
            "100000": { "0": 0.02, "1": 0.2888999, "5": 0.38889, "10": 0.588889, "50": 0.68889, "100": 0.78889, "500": 0.899955002, "0.01": 0.0444, "0.05": 0.17889, "0.1": 0.0333, "0.5": 0.15889 }
        },
        "weight": 7,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0005416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 2,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "AVAX_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.38, "5": 0.42, "10": 0.46, "50": 0.4888, "100": 0.52289, "500": 0.568999, "0.01": 0.22, "0.05": 0.32, "0.1": 0.34, "0.5": 0.36 },
            "1000": { "0": 0.1, "1": 0.8519, "5": 0.88994, "10": 0.89494, "50": 0.898984, "100": 0.899988, "500": 0.899994, "0.01": 0.14, "0.05": 0.422, "0.1": 0.575, "0.5": 0.8086 },
            "10000": { "0": 0.01, "1": 0.59805, "5": 0.817454, "10": 0.856743, "50": 0.8910028, "100": 0.8999801998, "500": 0.8999900999, "0.01": 0.017, "0.05": 0.081, "0.1": 0.1488, "0.5": 0.4478 },
            "100000": { "0": 0.0012, "1": 0.257304, "5": 0.600175, "10": 0.72124, "50": 0.85127328, "100": 0.89512392, "500": 0.8997634, "0.01": 0.0036, "0.05": 0.01768, "0.1": 0.03464, "0.5": 0.150108 }
        },
        "weight": 8,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0004416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 4,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "ADA_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.1, "1": 0.898, "5": 0.899, "10": 0.8998, "50": 0.8999, "100": 0.89998, "500": 0.89999, "0.01": 0.4889, "0.05": 0.72, "0.1": 0.81, "0.5": 0.89 },
            "1000": { "0": 0.1, "1": 0.8988, "5": 0.8994, "10": 0.89988, "50": 0.89994, "100": 0.899988, "500": 0.899994, "0.01": 0.3889, "0.05": 0.81, "0.1": 0.84, "0.5": 0.894 },
            "10000": { "0": 0.1, "1": 0.8980249802, "5": 0.8980249802, "10": 0.899801998, "50": 0.899900999, "100": 0.8999801998, "500": 0.8999900999, "0.01": 0.2889, "0.05": 0.8108, "0.1": 0.88062, "0.5": 0.8902089021 },
            "100000": { "0": 0.1, "1": 0.8910890911, "5": 0.8955223955, "10": 0.8991008991, "50": 0.8995502296, "100": 0.8999100099, "500": 0.899955002, "0.01": 0.15, "0.05": 0.6, "0.1": 0.8181836364, "0.5": 0.8571428571 }
        },
        "weight": 9,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0005416634436,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 5,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725379200000
    },
    "NOT_USDT": {
        "maxUserPositionAmount": 10000,
        "pnlFee": {
            "1": { "0": 0.0112, "1": 0.39253, "5": 0.421235, "10": 0.49235, "50": 0.55234, "100": 0.59345, "500": 0.598888, "0.01": 0.24123, "0.05": 0.24123, "0.1": 0.241234, "0.5": 0.29235 },
            "1000": { "0": 0.02234, "1": 0.3970001, "5": 0.4273234, "10": 0.4974423, "50": 0.557634, "100": 0.597772, "500": 0.79999, "0.01": 0.243234, "0.05": 0.24544234, "0.1": 0.2458234, "0.5": 0.2967324 },
            "10000": { "0": 0.1234, "1": 0.71, "5": 0.7444, "10": 0.7555, "50": 0.76555, "100": 0.77888, "500": 0.801, "0.01": 0.44234, "0.05": 0.56234, "0.1": 0.5634, "0.5": 0.68999 },
            "100000": { "0": 0.1, "1": 0.512424, "5": 0.522424, "10": 0.53244, "50": 0.54221, "100": 0.55114, "500": 0.899955002, "0.01": 0.15, "0.05": 0.4421, "0.1": 0.51233, "0.5": 0.511424 }
        },
        "weight": 0,
        "minWagerAmount": 0.01,
        "maxWagerAmount": 10000,
        "minUserPositionAmount": 1,
        "bustBuffer": 0.0004899,
        "bufferMultiplier": 1,
        "maxMultiplier": 1000,
        "precision": 6,
        "flatFee": {
            "1": 0.00035,
            "33334": 0.00036,
            "100000": 0.00037,
            "166667": 0.00038,
            "233334": 0.00039,
            "300000": 0.0004,
            "366667": 0.00041,
            "433334": 0.00042,
            "500000": 0.00043,
            "566667": 0.00044,
            "633334": 0.00045,
            "700000": 0.00046,
            "766667": 0.00047,
            "833334": 0.00048,
            "900001": 0.00049,
            "966667": 0.0005
        },
        "availableFees": {
            "pnl": true,
            "flat": true
        },
        "endOfFundingPeriod": 1725361200000
    }
};


// 下注记录 
var test_record = [
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f514a2084a1553a8041acd",
        "avatar": 0,
        "bustPrice": 0.11365477386607592,
        "closedAt": "2024-09-26T08:00:47.758Z",
        "currency": "usdt",
        "entryPrice": 0.11358,
        "exitPrice": 0.11357,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0.0004402183483005863,
        "positionAmount": 50,
        "roi": 0.8804366966011726,
        "state": "cashed",
        "timestamp": 1727337634500,
        "type": "short",
        "wager": 0.05,
        "validated": false
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f51481084a1553a8041ac9",
        "avatar": 0,
        "bustPrice": 0.11354470144905471,
        "closedAt": "2024-09-26T08:00:09.945Z",
        "currency": "usdt",
        "entryPrice": 0.11347,
        "exitPrice": 0.11344,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0.001982902969948152,
        "positionAmount": 10,
        "roi": 19.82902969948152,
        "state": "cashed",
        "timestamp": 1727337601000,
        "type": "short",
        "wager": 0.01,
        "validated": true
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51484084a1553a8041acc",
        "avatar": 0,
        "bustPrice": 63889.383044534276,
        "closedAt": "2024-09-26T08:00:06.340Z",
        "currency": "usdt",
        "entryPrice": 63847.350000000006,
        "exitPrice": 63825.3,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.17958458730082788,
        "positionAmount": 1000,
        "roi": 17.95845873008279,
        "state": "cashed",
        "timestamp": 1727337604000,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "632c67a60f7d816fa99393ea",
        "_id": "66f4bdaf084a1553a80416c5",
        "avatar": 0,
        "bustPrice": 50422.32513861024,
        "closedAt": "2024-09-26T07:59:12.830Z",
        "currency": "usdt",
        "entryPrice": 63001,
        "exitPrice": 63828.3,
        "feeType": "pnl",
        "funding": 0,
        "multi": 5,
        "name": "User9328474",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.03506120537769254,
        "positionAmount": 3,
        "roi": 5.8435342296154245,
        "state": "cashed",
        "stopLossAmount": 0.2,
        "stopLossPrice": 58821.023462702884,
        "takeProfitAmount": 0.04,
        "takeProfitPrice": 64000,
        "timestamp": 1727315375000,
        "type": "long",
        "wager": 0.6,
        "validated": true
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f513e1084a1553a8041aaf",
        "avatar": 0,
        "bustPrice": 0.11345464219876462,
        "closedAt": "2024-09-26T07:57:40.357Z",
        "currency": "usdt",
        "entryPrice": 0.11338,
        "exitPrice": 0.11333,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0.0033074616334446876,
        "positionAmount": 10,
        "roi": 33.07461633444688,
        "state": "cashed",
        "timestamp": 1727337441000,
        "type": "short",
        "wager": 0.01,
        "validated": true
    },
    {
        "userId": "65286dc77da85b481c2617fe",
        "_id": "66f5129d084a1553a8041aa1",
        "avatar": 0,
        "bustPrice": 65094.88623992693,
        "closedAt": "2024-09-26T07:56:56.527Z",
        "currency": "usdt",
        "entryPrice": 63839.9,
        "exitPrice": 63794.350000000006,
        "feeType": "pnl",
        "funding": 0,
        "multi": 50,
        "name": "User5574651",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.03182226162634661,
        "positionAmount": 50,
        "roi": 3.1822261626346613,
        "state": "cashed",
        "takeProfitAmount": 0.37,
        "takeProfitPrice": 63300,
        "timestamp": 1727337117500,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51359084a1553a8041aab",
        "avatar": 0,
        "bustPrice": 63211.230591779065,
        "closedAt": "2024-09-26T07:55:55.179Z",
        "currency": "usdt",
        "entryPrice": 63827.7,
        "exitPrice": 63825.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.0014100461085043284,
        "positionAmount": 50,
        "roi": -0.2820092217008657,
        "state": "cashed",
        "timestamp": 1727337305500,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f5135a084a1553a8041aac",
        "avatar": 0,
        "bustPrice": 63195.1375397481,
        "closedAt": "2024-09-26T07:55:55.106Z",
        "currency": "usdt",
        "entryPrice": 63811.45,
        "exitPrice": 63825.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.010099598112880914,
        "positionAmount": 50,
        "roi": 2.019919622576183,
        "state": "cashed",
        "timestamp": 1727337306000,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51366084a1553a8041aad",
        "avatar": 0,
        "bustPrice": 63867.91892321513,
        "closedAt": "2024-09-26T07:55:20.423Z",
        "currency": "usdt",
        "entryPrice": 63825.9,
        "exitPrice": 63827.7,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.028201717484526413,
        "positionAmount": 1000,
        "roi": -2.8201717484526414,
        "state": "cashed",
        "timestamp": 1727337318000,
        "type": "short",
        "wager": 1,
        "validated": false
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f51355084a1553a8041aaa",
        "avatar": 0,
        "bustPrice": 0.11322541046815987,
        "closedAt": "2024-09-26T07:55:19.386Z",
        "currency": "usdt",
        "entryPrice": 0.1133,
        "exitPrice": 0.11331,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0.00017652250661952555,
        "positionAmount": 20,
        "roi": 0.8826125330976277,
        "state": "cashed",
        "timestamp": 1727337301500,
        "type": "long",
        "wager": 0.02,
        "validated": false
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f5133f084a1553a8041aa9",
        "avatar": 0,
        "bustPrice": 0.11325539071806319,
        "closedAt": "2024-09-26T07:54:52.710Z",
        "currency": "usdt",
        "entryPrice": 0.11333,
        "exitPrice": 0.11334,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0.00008823788934965253,
        "positionAmount": 10,
        "roi": 0.8823788934965253,
        "state": "cashed",
        "timestamp": 1727337279500,
        "type": "long",
        "wager": 0.01,
        "validated": false
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f51326084a1553a8041aa8",
        "avatar": 0,
        "bustPrice": 0.11341461586530237,
        "closedAt": "2024-09-26T07:54:22.568Z",
        "currency": "usdt",
        "entryPrice": 0.11334,
        "exitPrice": 0.11341461586530237,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": -0.01,
        "positionAmount": 10,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727337254000,
        "type": "short",
        "wager": 0.01,
        "validated": true
    },
    {
        "userId": "62251a360fa8753e82ff288f",
        "_id": "66f51309084a1553a8041aa7",
        "avatar": 0,
        "bustPrice": 0.11329536438460093,
        "closedAt": "2024-09-26T07:53:57.920Z",
        "currency": "usdt",
        "entryPrice": 0.11337,
        "exitPrice": 0.11337,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User7402793",
        "pair": "DOGE_USDT",
        "pairCurrency": "doge",
        "pnl": 0,
        "positionAmount": 10,
        "roi": 0,
        "state": "cashed",
        "timestamp": 1727337225000,
        "type": "long",
        "wager": 0.01,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f512ac084a1553a8041aa3",
        "avatar": 0,
        "bustPrice": 63222.42145257598,
        "closedAt": "2024-09-26T07:52:59.577Z",
        "currency": "usdt",
        "entryPrice": 63839,
        "exitPrice": 63832.4,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.005169253904351998,
        "positionAmount": 50,
        "roi": -1.0338507808703996,
        "state": "cashed",
        "timestamp": 1727337132500,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f512ad084a1553a8041aa4",
        "avatar": 0,
        "bustPrice": 63224.550687152376,
        "closedAt": "2024-09-26T07:52:59.497Z",
        "currency": "usdt",
        "entryPrice": 63841.149999999994,
        "exitPrice": 63832.4,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.006852946727927619,
        "positionAmount": 50,
        "roi": -1.3705893455855238,
        "state": "cashed",
        "timestamp": 1727337133000,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51241084a1553a8041a9e",
        "avatar": 0,
        "bustPrice": 63217.469744258764,
        "closedAt": "2024-09-26T07:51:30.796Z",
        "currency": "usdt",
        "entryPrice": 63834,
        "exitPrice": 63835.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.00028276467086527286,
        "positionAmount": 50,
        "roi": 0.05655293417305457,
        "state": "cashed",
        "timestamp": 1727337025500,
        "type": "long",
        "wager": 0.5,
        "validated": true
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51241084a1553a8041a9d",
        "avatar": 0,
        "bustPrice": 63217.469744258764,
        "closedAt": "2024-09-26T07:51:30.723Z",
        "currency": "usdt",
        "entryPrice": 63834,
        "exitPrice": 63835.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.00028276467086527286,
        "positionAmount": 50,
        "roi": 0.05655293417305457,
        "state": "cashed",
        "timestamp": 1727337025500,
        "type": "long",
        "wager": 0.5,
        "validated": true
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51276084a1553a8041aa0",
        "avatar": 0,
        "bustPrice": 63871.921556561356,
        "closedAt": "2024-09-26T07:51:20.149Z",
        "currency": "usdt",
        "entryPrice": 63829.9,
        "exitPrice": 63840.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.17233302887831564,
        "positionAmount": 1000,
        "roi": -17.233302887831563,
        "state": "cashed",
        "timestamp": 1727337078000,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f5125a084a1553a8041a9f",
        "avatar": 73,
        "bustPrice": 64464.11016036792,
        "closedAt": "2024-09-26T07:50:58.020Z",
        "currency": "usdt",
        "entryPrice": 63847.45,
        "exitPrice": 63834.1,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.09200054191669925,
        "positionAmount": 500,
        "roi": 1.840010838333985,
        "state": "cashed",
        "timestamp": 1727337050500,
        "type": "short",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f5121f084a1553a8041a9c",
        "avatar": 73,
        "bustPrice": 64444.67423738921,
        "closedAt": "2024-09-26T07:50:14.281Z",
        "currency": "usdt",
        "entryPrice": 63828.2,
        "exitPrice": 63841.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.10731933534083968,
        "positionAmount": 500,
        "roi": -2.146386706816793,
        "state": "cashed",
        "timestamp": 1727336991000,
        "type": "short",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f5116d084a1553a8041a98",
        "avatar": 0,
        "bustPrice": 63178.747385218114,
        "closedAt": "2024-09-26T07:49:45.586Z",
        "currency": "usdt",
        "entryPrice": 63794.9,
        "exitPrice": 63817.3,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.015660186002330358,
        "positionAmount": 50,
        "roi": 3.1320372004660717,
        "state": "cashed",
        "timestamp": 1727336813500,
        "type": "long",
        "wager": 0.5,
        "validated": true
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f5116d084a1553a8041a97",
        "avatar": 0,
        "bustPrice": 63181.86696145796,
        "closedAt": "2024-09-26T07:49:45.513Z",
        "currency": "usdt",
        "entryPrice": 63798.05,
        "exitPrice": 63817.3,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.013457307864425323,
        "positionAmount": 50,
        "roi": 2.6914615728850646,
        "state": "cashed",
        "timestamp": 1727336813000,
        "type": "long",
        "wager": 0.5,
        "validated": true
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51205084a1553a8041a9b",
        "avatar": 0,
        "bustPrice": 63860.013722356336,
        "closedAt": "2024-09-26T07:49:26.887Z",
        "currency": "usdt",
        "entryPrice": 63818,
        "exitPrice": 63819.3,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.020370428405824537,
        "positionAmount": 1000,
        "roi": -2.037042840582454,
        "state": "cashed",
        "timestamp": 1727336965000,
        "type": "short",
        "wager": 1,
        "validated": false
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f51149084a1553a8041a96",
        "avatar": 73,
        "bustPrice": 64417.11056480122,
        "closedAt": "2024-09-26T07:48:16.425Z",
        "currency": "usdt",
        "entryPrice": 63800.9,
        "exitPrice": 63803.1,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.017241136096803413,
        "positionAmount": 500,
        "roi": -0.3448227219360683,
        "state": "cashed",
        "takeProfitAmount": 0.07,
        "takeProfitPrice": 63790.74985681818,
        "timestamp": 1727336777500,
        "type": "short",
        "wager": 5,
        "validated": true
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f5103d084a1553a8041a83",
        "avatar": 73,
        "bustPrice": 63201.12910681194,
        "closedAt": "2024-09-26T07:48:02.318Z",
        "currency": "usdt",
        "entryPrice": 63817.5,
        "exitPrice": 63801.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.12222352802913422,
        "positionAmount": 500,
        "roi": -2.444470560582684,
        "state": "cashed",
        "takeProfitAmount": 0.1,
        "takeProfitPrice": 63832.003977272725,
        "timestamp": 1727336509000,
        "type": "long",
        "wager": 5,
        "validated": true
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51189084a1553a8041a99",
        "avatar": 0,
        "bustPrice": 63837.699041451124,
        "closedAt": "2024-09-26T07:47:23.616Z",
        "currency": "usdt",
        "entryPrice": 63795.7,
        "exitPrice": 63797,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.020377548957106993,
        "positionAmount": 1000,
        "roi": -2.0377548957106995,
        "state": "cashed",
        "timestamp": 1727336841500,
        "type": "short",
        "wager": 1,
        "validated": false
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f51128084a1553a8041a95",
        "avatar": 73,
        "bustPrice": 64406.96349851884,
        "closedAt": "2024-09-26T07:45:48.577Z",
        "currency": "usdt",
        "entryPrice": 63790.850000000006,
        "exitPrice": 63787.7,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.00246900613489923,
        "positionAmount": 500,
        "roi": 0.049380122697984606,
        "state": "cashed",
        "takeProfitAmount": 0.1,
        "takeProfitPrice": 63776.35207954546,
        "timestamp": 1727336744000,
        "type": "short",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f510db084a1553a8041a93",
        "avatar": 0,
        "bustPrice": 721.4903588290533,
        "closedAt": "2024-09-26T07:44:28.500Z",
        "currency": "usdc",
        "entryPrice": 721.7213096481407,
        "exitPrice": 721.4903588290533,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336667500,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f510d7084a1553a8041a92",
        "avatar": 0,
        "bustPrice": 720.4477537177983,
        "closedAt": "2024-09-26T07:44:24.000Z",
        "currency": "usdc",
        "entryPrice": 720.6783707964531,
        "exitPrice": 720.4477537177983,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336663500,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f51091084a1553a8041a8e",
        "avatar": 0,
        "bustPrice": 722.0077245373145,
        "closedAt": "2024-09-26T07:44:21.000Z",
        "currency": "usdc",
        "entryPrice": 721.7767559754024,
        "exitPrice": 722.0077245373145,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "takeProfitAmount": 101.05,
        "takeProfitPrice": 645,
        "timestamp": 1727336593500,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51097084a1553a8041a8f",
        "avatar": 0,
        "bustPrice": 64388.9410972113,
        "closedAt": "2024-09-26T07:44:06.375Z",
        "currency": "usdt",
        "entryPrice": 63773,
        "exitPrice": 63801.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.022658491838239894,
        "positionAmount": 50,
        "roi": -4.531698367647978,
        "state": "cashed",
        "timestamp": 1727336599000,
        "type": "short",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51097084a1553a8041a90",
        "avatar": 0,
        "bustPrice": 64389.24399471227,
        "closedAt": "2024-09-26T07:44:05.799Z",
        "currency": "usdt",
        "entryPrice": 63773.3,
        "exitPrice": 63801.9,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.022423177097624352,
        "positionAmount": 50,
        "roi": -4.484635419524871,
        "state": "cashed",
        "timestamp": 1727336599500,
        "type": "short",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f5109d084a1553a8041a91",
        "avatar": 73,
        "bustPrice": 63160.52509861076,
        "closedAt": "2024-09-26T07:43:51.199Z",
        "currency": "usdt",
        "entryPrice": 63776.5,
        "exitPrice": 63799.69145454546,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.16000000000001355,
        "positionAmount": 500,
        "roi": 3.2000000000002706,
        "state": "profit",
        "takeProfitAmount": 0.16,
        "takeProfitPrice": 63799.69145454546,
        "timestamp": 1727336605500,
        "type": "long",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "66c4ba89010dbf08145f120f",
        "_id": "66f51091084a1553a8041a8d",
        "avatar": 0,
        "bustPrice": 63843.9531560546,
        "closedAt": "2024-09-26T07:43:15.979Z",
        "currency": "usdt",
        "entryPrice": 63801.95,
        "exitPrice": 63779.8,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "User4874415",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.18052739767353465,
        "positionAmount": 1000,
        "roi": 18.052739767353465,
        "state": "cashed",
        "timestamp": 1727336593500,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f5108f084a1553a8041a8c",
        "avatar": 0,
        "bustPrice": 721.0713769773491,
        "closedAt": "2024-09-26T07:43:11.500Z",
        "currency": "usdc",
        "entryPrice": 720.8407079508049,
        "exitPrice": 721.0713769773491,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336591000,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f5108d084a1553a8041a8b",
        "avatar": 0,
        "bustPrice": 720.2068801694073,
        "closedAt": "2024-09-26T07:43:09.500Z",
        "currency": "usdc",
        "entryPrice": 719.9764876933455,
        "exitPrice": 720.2068801694073,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336589000,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f51062084a1553a8041a8a",
        "avatar": 0,
        "bustPrice": 712.5467921519096,
        "closedAt": "2024-09-26T07:42:48.500Z",
        "currency": "usdc",
        "entryPrice": 712.7748801135459,
        "exitPrice": 712.5467921519096,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336546000,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51042084a1553a8041a85",
        "avatar": 0,
        "bustPrice": 64432.45737151688,
        "closedAt": "2024-09-26T07:42:33.138Z",
        "currency": "usdt",
        "entryPrice": 63816.1,
        "exitPrice": 63819.1,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.0023505040264133973,
        "positionAmount": 50,
        "roi": -0.47010080528267945,
        "state": "cashed",
        "timestamp": 1727336514500,
        "type": "short",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f51042084a1553a8041a84",
        "avatar": 0,
        "bustPrice": 64432.45737151688,
        "closedAt": "2024-09-26T07:42:32.563Z",
        "currency": "usdt",
        "entryPrice": 63816.1,
        "exitPrice": 63819.1,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.0023505040264133973,
        "positionAmount": 50,
        "roi": -0.47010080528267945,
        "state": "cashed",
        "timestamp": 1727336514500,
        "type": "short",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f5105e084a1553a8041a89",
        "avatar": 0,
        "bustPrice": 710.7673424095278,
        "closedAt": "2024-09-26T07:42:24.500Z",
        "currency": "usdc",
        "entryPrice": 710.5399696192496,
        "exitPrice": 710.7673424095278,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336542500,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f5105b084a1553a8041a88",
        "avatar": 0,
        "bustPrice": 709.185743375819,
        "closedAt": "2024-09-26T07:42:19.500Z",
        "currency": "usdc",
        "entryPrice": 708.9588765353277,
        "exitPrice": 709.185743375819,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336539000,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f5104b084a1553a8041a87",
        "avatar": 0,
        "bustPrice": 709.2012122082047,
        "closedAt": "2024-09-26T07:42:09.000Z",
        "currency": "usdc",
        "entryPrice": 709.4282292415619,
        "exitPrice": 709.2012122082047,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336523500,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f51049084a1553a8041a86",
        "avatar": 0,
        "bustPrice": 713.6425562355498,
        "closedAt": "2024-09-26T07:42:02.000Z",
        "currency": "usdc",
        "entryPrice": 713.8709949539351,
        "exitPrice": 713.6425562355498,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336521000,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f51013084a1553a8041a81",
        "avatar": 0,
        "bustPrice": 714.4377790339145,
        "closedAt": "2024-09-26T07:41:58.500Z",
        "currency": "usdc",
        "entryPrice": 714.209232079649,
        "exitPrice": 714.4377790339145,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "takeProfitAmount": 165.21,
        "takeProfitPrice": 590,
        "timestamp": 1727336467500,
        "type": "short",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f51021084a1553a8041a82",
        "avatar": 73,
        "bustPrice": 63185.38267436319,
        "closedAt": "2024-09-26T07:41:31.329Z",
        "currency": "usdt",
        "entryPrice": 63801.6,
        "exitPrice": 63804.8,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": 0.0025077740997125203,
        "positionAmount": 500,
        "roi": 0.050155481994250406,
        "state": "cashed",
        "timestamp": 1727336481500,
        "type": "long",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "65a0286e02efa90034a9fb34",
        "_id": "66f50ff3084a1553a8041a7f",
        "avatar": 73,
        "bustPrice": 63206.27888346185,
        "closedAt": "2024-09-26T07:41:13.660Z",
        "currency": "usdt",
        "entryPrice": 63822.7,
        "exitPrice": 63813.55,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "DxSourav",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.07168295919785735,
        "positionAmount": 500,
        "roi": -1.4336591839571469,
        "state": "cashed",
        "takeProfitAmount": 0.01,
        "takeProfitPrice": 63835.46453999999,
        "timestamp": 1727336435000,
        "type": "long",
        "wager": 5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f50fee084a1553a8041a7d",
        "avatar": 0,
        "bustPrice": 63206.179849295506,
        "closedAt": "2024-09-26T07:41:10.576Z",
        "currency": "usdt",
        "entryPrice": 63822.6,
        "exitPrice": 63809,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.010654533033751794,
        "positionAmount": 50,
        "roi": -2.130906606750359,
        "state": "cashed",
        "timestamp": 1727336429500,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "60d50e158a8f0a19d4a8d13d",
        "_id": "66f50fed084a1553a8041a7c",
        "avatar": 0,
        "bustPrice": 63206.179849295506,
        "closedAt": "2024-09-26T07:41:10.508Z",
        "currency": "usdt",
        "entryPrice": 63822.6,
        "exitPrice": 63809,
        "feeType": "pnl",
        "funding": 0,
        "multi": 100,
        "name": "User400174",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "pnl": -0.010654533033751794,
        "positionAmount": 50,
        "roi": -2.130906606750359,
        "state": "cashed",
        "timestamp": 1727336429500,
        "type": "long",
        "wager": 0.5,
        "validated": false
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f50ff4084a1553a8041a80",
        "avatar": 0,
        "bustPrice": 721.9354278914142,
        "closedAt": "2024-09-26T07:41:00.500Z",
        "currency": "usdc",
        "entryPrice": 722.1665211781911,
        "exitPrice": 721.9354278914142,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336436000,
        "type": "long",
        "wager": 1,
        "validated": true
    },
    {
        "userId": "5fac912e51812b0ac4f55db3",
        "_id": "66f50ff1084a1553a8041a7e",
        "avatar": 0,
        "bustPrice": 722.289805569407,
        "closedAt": "2024-09-26T07:40:34.000Z",
        "currency": "usdc",
        "entryPrice": 722.5210122933408,
        "exitPrice": 722.289805569407,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "smokedout",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "pnl": -1,
        "positionAmount": 1000,
        "roi": -100,
        "state": "busted",
        "timestamp": 1727336433500,
        "type": "long",
        "wager": 1,
        "validated": true
    }
];

// ====================================================================================
// ====================================================================================
// ====================================================================================
// ====================================================================================

var leaderboard = [
    {
        "userId": "64fdcbf0deea68223d7c0b21",
        "_id": "65ba709ffd316414a42db227",
        "avatar": 0,
        "bustPrice": 49204.13655613398,
        "closedAt": "2024-03-12T17:16:04.629Z",
        "currency": "usdt",
        "entryPrice": 43508.3284523,
        "exitPrice": 69835.90722011,
        "feeType": "flat",
        "flatFee": 2.8,
        "funding": -902.8569472322922,
        "multi": 20,
        "name": "Puzzle",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 1,
        "pnl": 2122.72225844485,
        "positionAmount": 5000,
        "roi": 1210.231682270857,
        "state": "cashed",
        "timestamp": 1706717343000,
        "type": "long",
        "wager": 250
    },
    {
        "userId": "64fdcbf0deea68223d7c0b21",
        "_id": "65ba7593fd316414a42db28b",
        "avatar": 0,
        "bustPrice": 44465.57215365248,
        "closedAt": "2024-03-12T17:16:02.602Z",
        "currency": "usdt",
        "entryPrice": 43431.91088253,
        "exitPrice": 69801.97870719,
        "feeType": "flat",
        "flatFee": 2.5701989530721097,
        "funding": -1025.591616379677,
        "multi": 5,
        "name": "Puzzle",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 2,
        "pnl": 1761.0493965372275,
        "positionAmount": 4589.640987628767,
        "roi": 303.5794107238679,
        "state": "cashed",
        "timestamp": 1706718611000,
        "type": "long",
        "wager": 917.9281975257534
    },
    {
        "userId": "6644e1c9bd56c660c966f724",
        "_id": "6669f5f35ee9043949e165a3",
        "avatar": 0,
        "bustPrice": 69238.51318640629,
        "closedAt": "2024-06-12T19:47:51.270Z",
        "currency": "usdt",
        "entryPrice": 69192.96093078,
        "exitPrice": 67763,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "Curyz",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 3,
        "pnl": 1841.5494422792217,
        "positionAmount": 100000,
        "roi": 1841.5494422792217,
        "state": "profit",
        "stopLossAmount": 55.99,
        "stopLossPrice": 69208.05,
        "takeProfitAmount": 1841.5494422792217,
        "takeProfitPrice": 67763,
        "timestamp": 1718220275500,
        "type": "short",
        "wager": 100,
        "validated": true
    },
    {
        "userId": "6644e1c9bd56c660c966f724",
        "_id": "6669f5f45ee9043949e165a4",
        "avatar": 0,
        "bustPrice": 69238.51318640629,
        "closedAt": "2024-06-12T19:47:48.161Z",
        "currency": "usdt",
        "entryPrice": 69192.96093078,
        "exitPrice": 67862.99,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "Curyz",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 4,
        "pnl": 1712.7791208180133,
        "positionAmount": 100000,
        "roi": 1712.7791208180136,
        "state": "profit",
        "stopLossAmount": 56,
        "stopLossPrice": 69208.05,
        "takeProfitAmount": 1712.7791208180133,
        "takeProfitPrice": 67862.99,
        "timestamp": 1718220275500,
        "type": "short",
        "wager": 100,
        "validated": true
    },
    {
        "userId": "62822198e1970ea6a1cb78d0",
        "_id": "66e027b9144bfdf50bb7cbd9",
        "avatar": 123,
        "bustPrice": 846.8702516898492,
        "closedAt": "2024-09-10T11:04:28.500Z",
        "currency": "usdt",
        "entryPrice": 846.5993399010808,
        "exitPrice": 842.8864597172133,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "Octobomb",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "place": 5,
        "pnl": 1374.356607785366,
        "positionAmount": 329870,
        "roi": 416.6358285947088,
        "state": "cashed",
        "timestamp": 1725966265500,
        "type": "short",
        "wager": 329.87,
        "validated": true
    },
    {
        "userId": "63daa73b06934c36e6b9e1d8",
        "_id": "65c5fe85b7ec48ae4a9f5965",
        "avatar": 0,
        "bustPrice": 2464.0603632716043,
        "closedAt": "2024-02-18T17:55:17.818Z",
        "currency": "usdt",
        "entryPrice": 2462.97255833,
        "exitPrice": 2811.85081772,
        "feeType": "pnl",
        "funding": -10,
        "multi": 1000,
        "name": "User5049372",
        "pair": "ETH_USDT",
        "pairCurrency": "eth",
        "place": 6,
        "pnl": 1271.659428225734,
        "positionAmount": 10000,
        "roi": 12816.594282257342,
        "state": "cashed",
        "timestamp": 1707474565000,
        "type": "long",
        "wager": 10
    },
    {
        "userId": "61af3dead00f461b460f55f1",
        "_id": "65cb7884d5fcbae708c8f4f4",
        "avatar": "64e72283fc8ad8651e70f536",
        "bustPrice": 42007.76,
        "closedAt": "2024-02-26T13:25:00.000Z",
        "currency": "usdt",
        "entryPrice": 42035.44,
        "exitPrice": 51393.28,
        "feeType": "pnl",
        "funding": -18.96,
        "multi": 1000,
        "name": "TronCruise",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 7,
        "pnl": 1066.32,
        "positionAmount": 10000,
        "roi": 10663.17,
        "state": "cashed",
        "timestamp": 1706789460000,
        "type": "long",
        "wager": 10
    },
    {
        "userId": "6220593795abbd62d56bccc7",
        "_id": "66e876c01f552623d445670e",
        "avatar": 0,
        "bustPrice": 58025.747766444365,
        "closedAt": "2024-09-20T06:20:01.282Z",
        "currency": "usdc",
        "entryPrice": 57728.03904661,
        "exitPrice": 63715.06320845,
        "feeType": "pnl",
        "funding": -98.15427023200482,
        "multi": 200,
        "name": "Jungle",
        "pair": "BTC_USDT",
        "pairCurrency": "btc",
        "place": 8,
        "pnl": 834.1036229829173,
        "positionAmount": 10000,
        "roi": 1864.515786429844,
        "state": "cashed",
        "timestamp": 1726510784000,
        "type": "long",
        "wager": 50,
        "validated": true
    },
    {
        "userId": "6466bd7f3658fae6b3e9e2e8",
        "_id": "6623a4a158f5622f068f91ab",
        "avatar": 131,
        "bustPrice": 1121.9754972979763,
        "closedAt": "2024-04-20T11:39:29.500Z",
        "currency": "usdt",
        "entryPrice": 1121.6165799923788,
        "exitPrice": 1016.7760059379592,
        "feeType": "pnl",
        "funding": 0,
        "multi": 1000,
        "name": "SJoyWalter",
        "pair": "FuryWaves_USDT",
        "pairCurrency": "furywaves",
        "place": 9,
        "pnl": 887.9910223186548,
        "positionAmount": 10000,
        "roi": 8879.910223186547,
        "state": "cashed",
        "timestamp": 1713611936000,
        "type": "short",
        "wager": 10
    },
    {
        "bold": true,
        "userId": "6566c714572c629a6d05e562",
        "_id": "66f50e0c084a1553a8041a61",
        "avatar": 0,
        "bustPrice": 0.2924497022827842,
        "closedAt": "2024-09-26T07:39:52.358Z",
        "currency": "usdt",
        "entryPrice": 0.5845,
        "exitPrice": 0.5862,
        "feeType": "pnl",
        "funding": 0,
        "multi": 2,
        "name": "User6976774",
        "pair": "XRP_USDT",
        "pairCurrency": "xrp",
        "place": 1292892,
        "pnl": 0.0023558597091531707,
        "positionAmount": 1,
        "roi": 0.47117194183063416,
        "state": "cashed",
        "stopLossAmount": 0.1,
        "stopLossPrice": 0.5262297320545057,
        "takeProfitAmount": 0.1,
        "takeProfitPrice": 0.6494588797510558,
        "timestamp": 1727335948000,
        "type": "long",
        "wager": 0.5,
        "validated": true
    }
];


